<template>
  <div class="reverse-proxy-container">
    <!-- 服务器信息卡片 -->
    <div class="settings-card">
      <div class="card-header">
        <h3 class="card-title">{{ $t('reverseProxy.title') }}</h3>
      </div>
      <div class="card-body">
        <p class="description-text">
          {{ $t('reverseProxy.description') }}
        </p>
      </div>
    </div>

    <!-- 反向代理列表卡片 -->
    <div class="settings-card">
      <div class="card-header">
        <div class="header-content">
          <h3 class="card-title">{{ $t('reverseProxy.proxyList') }}</h3>
          <div class="btn-group">
            <el-button type="info" size="small" @click="showLogDialog">
              <el-icon><Document /></el-icon>
              {{ $t('reverseProxy.viewLogs') }}
            </el-button>
            <el-button type="primary" size="small" @click="showAddDialog">{{ $t('reverseProxy.add') }}</el-button>
            <el-button type="info" size="small" @click="refreshData">{{ $t('reverseProxy.refresh') }}</el-button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div v-if="isLoading" class="loading-overlay">
          <el-icon class="is-loading"><Loading /></el-icon>
          {{ $t('reverseProxy.loading') }}
        </div>

        <div v-if="!isLoading && proxyList.length === 0" class="empty-state">
          <el-icon><Box /></el-icon>
          <p>{{ $t('reverseProxy.noProxyConfig') }}</p>
        </div>

        <div v-if="proxyList.length > 0" class="table-responsive">
          <table class="proxy-table">
            <thead>
              <tr>
                <th>{{ $t('reverseProxy.name') }}</th>
                <th>{{ $t('reverseProxy.internalAddress') }}</th>
                <th>{{ $t('reverseProxy.internalPort') }}</th>
                <th>{{ $t('reverseProxy.mappingPort') }}</th>
                <th>{{ $t('reverseProxy.protocol') }}</th>
                <th>{{ $t('reverseProxy.status') }}</th>
                <th>{{ $t('reverseProxy.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in proxyList" :key="item.name" :class="{ 'stopped-row': !item.switch }">
                <td data-label="名称">{{ item.name }}</td>
                <td data-label="内网地址">{{ item.targetIP }}</td>
                <td data-label="内网端口">{{ item.targetPort }}</td>
                <td data-label="映射端口">{{ item.mappingPort }}</td>
                <td data-label="协议">{{ item.protocol }}</td>
                <td data-label="状态" class="status-cell">
                  <div class="status-content" :class="getStatusClass(item.status)">
                    {{ item.status }}
                  </div>
                </td>
                <td data-label="操作" class="action-cell">
                  <div class="action-buttons">
                    <template v-if="item.switch">
                      <el-button
                        type="warning"
                        size="small"
                        @click="stopProxy(item)"
                      >
                        {{ $t('reverseProxy.stop') }}
                      </el-button>
                    </template>
                    <template v-else>
                      <el-button
                        type="success"
                        size="small"
                        @click="startProxy(item)"
                      >
                        {{ $t('reverseProxy.start') }}
                      </el-button>
                      <el-button
                        type="primary"
                        size="small"
                        @click="showEditDialog(item)"
                      >
                        {{ $t('reverseProxy.edit') }}
                      </el-button>
                      <el-button
                        type="danger"
                        size="small"
                        @click="deleteProxy(item)"
                      >
                        {{ $t('reverseProxy.delete') }}
                      </el-button>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 新增/编辑代理对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      :width="isMobile ? '95%' : '600px'"
      :before-close="handleCloseDialog"
      :destroy-on-close="true"
      class="proxy-dialog"
    >
      <el-form
        :model="proxyForm"
        :rules="formRules"
        ref="proxyFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('reverseProxy.name')" prop="name">
          <el-input
            v-model="proxyForm.name"
            :placeholder="$t('reverseProxy.namePlaceholder')"
            :disabled="isEditing"
          />
        </el-form-item>

        <el-form-item :label="$t('reverseProxy.internalAddress')" prop="targetIP">
          <el-input v-model="proxyForm.targetIP" :placeholder="$t('reverseProxy.internalIpPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('reverseProxy.internalPort')" prop="targetPort">
          <el-input
            v-model.number="proxyForm.targetPort"
            type="number"
            :placeholder="$t('reverseProxy.internalPortPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('reverseProxy.mappingPort')" prop="mappingPort">
          <el-input
            v-model.number="proxyForm.mappingPort"
            type="number"
            :placeholder="$t('reverseProxy.mappingPortPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('reverseProxy.protocol')" prop="protocol">
          <el-select v-model="proxyForm.protocol" :placeholder="$t('reverseProxy.protocolRequired')">
            <el-option label="TCP" value="TCP" />
            <el-option label="UDP" value="UDP" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ $t('reverseProxy.cancel') }}</el-button>
          <el-button type="primary" @click="submitProxyForm">{{ $t('reverseProxy.save') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 反向代理日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('reverseProxy.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('reverseProxy.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('reverseProxy.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('reverseProxy.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('reverseProxy.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('reverseProxy.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('reverseProxy.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('reverseProxy.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('reverseProxy.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Loading, Box, Document, InfoFilled } from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config";
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default {
  name: 'ReverseProxy',
  components: { Loading, Box, Document, InfoFilled },
  setup() {
    const { t: i18n_t } = useI18n()
    const router = useRouter()
    const proxyList = ref([])
    const isLoading = ref(false)
    const intervalId = ref(null)
    const dialogVisible = ref(false)
    const isEditing = ref(false)
    const proxyFormRef = ref(null)
    const isEditingForm = ref(false)
    const dialogTitle = ref(i18n_t('reverseProxy.addProxy'))
    const currentEditItem = ref(null)

    const isMobile = ref(false)
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    // 页面状态
    const isLogin = ref(false)
    const loginUsername = ref('')
    const isAdmin = ref(false)
    
    // 表单数据
    const proxyForm = ref({
      name: '',
      targetIP: '127.0.0.1',
      targetPort: '',
      mappingPort: '',
      protocol: 'TCP'
    })
    
    // 日志对话框相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true) // 默认开启自动刷新
    const logRefreshInterval = ref(null)
    
    // 表单验证规则
    const formRules = {
      name: [
        { required: true, message: i18n_t('reverseProxy.nameRequired'), trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9]{3,15}$/, message: i18n_t('reverseProxy.nameFormatError'), trigger: 'blur' }
      ],
      targetIP: [
        { required: true, message: i18n_t('reverseProxy.ipRequired'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            const exp = /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/;
            if (exp.test(value)) {
              callback()
            } else {
              callback(new Error(i18n_t('reverseProxy.ipFormatError')))
            }
          },
          trigger: 'blur'
        }
      ],
      targetPort: [
        { required: true, message: i18n_t('reverseProxy.internalPortRequired'), trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: i18n_t('reverseProxy.internalPortRangeError'), trigger: 'blur' }
      ],
      mappingPort: [
        { required: true, message: i18n_t('reverseProxy.mappingPortRequired'), trigger: 'blur' },
        { type: 'number', min: 0, max: 65535, message: i18n_t('reverseProxy.mappingPortRangeError'), trigger: 'blur' }
      ],
      protocol: [
        { required: true, message: i18n_t('reverseProxy.protocolRequired'), trigger: 'change' }
      ]
    }
    
    // 获取状态样式类
    const getStatusClass = (status) => {
      if (!status) return 'stopped'

      const lowerStatus = status.toLowerCase()

      if (lowerStatus.includes('failed')) {
        return 'failed'
      } else if (lowerStatus.includes('success') || lowerStatus.includes('running')) {
        return 'running'
      } else {
        return 'stopped'
      }
    }
    
    // 显示新增对话框
    const showAddDialog = () => {
      isEditing.value = false
      dialogTitle.value = i18n_t('reverseProxy.addProxy')
      proxyForm.value = {
        name: '',
        targetIP: '127.0.0.1',
        targetPort: '',
        mappingPort: '',
        protocol: 'TCP'
      }
      dialogVisible.value = true
    }
    
    // 显示编辑对话框
    const showEditDialog = (item) => {
      if (item.switch) {
        ElMessage.warning(i18n_t('reverseProxy.pleaseStopFirst'))
        return
      }

      isEditing.value = true
      dialogTitle.value = i18n_t('reverseProxy.editProxy')
      currentEditItem.value = item
      proxyForm.value = {
        name: item.name,
        targetIP: item.targetIP,
        targetPort: item.targetPort,
        mappingPort: item.mappingPort,
        protocol: item.protocol
      }
      dialogVisible.value = true
    }
    
    // 关闭对话框前的确认
    const handleCloseDialog = (done) => {
      ElMessageBox.confirm(i18n_t('reverseProxy.confirmDiscard'), i18n_t('reverseProxy.confirm'), {
        confirmButtonText: i18n_t('reverseProxy.confirm'),
        cancelButtonText: i18n_t('reverseProxy.cancelText'),
        type: 'warning'
      }).then(() => {
        done()
      }).catch(() => {})
    }
    
    // 提交表单
    const submitProxyForm = async () => {
      try {
        await proxyFormRef.value.validate()

        const url = isEditing.value ? '/update-reverse-proxy' : '/add-reverse-proxy'
        const method = 'POST'

        const params = new URLSearchParams()
        params.append('name', proxyForm.value.name)
        params.append('targetIP', proxyForm.value.targetIP)
        params.append('targetPort', proxyForm.value.targetPort)
        params.append('mappingPort', proxyForm.value.mappingPort)
        params.append('protocol', proxyForm.value.protocol)

        // 如果是编辑，保留原始名称用于更新
        if (isEditing.value && currentEditItem.value) {
          params.append('originalName', currentEditItem.value.name)
        }

        const response = await fetch(`${API_BASE_URL}${url}`, {
          method,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('reverseProxy.operationFailed'))
        }

        const data = await response.json()
        if (data.errCode === 0) {
          ElMessage.success(data.errMsg || i18n_t('reverseProxy.operationSuccess'))
        } else {
          ElMessage.error(data.errMsg || i18n_t('reverseProxy.operationFailed'))
        }
        dialogVisible.value = false
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('reverseProxy.operationFailed'))
      }
    }
    
    // 启动代理
    const startProxy = async (item) => {
      try {
        const params = new URLSearchParams()
        params.append('name', item.name)

        const response = await fetch(`${API_BASE_URL}/start-reverse-proxy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('reverseProxy.startFailed'))
        }

        const data = await response.json()
        if (data.errCode === 0) {
          ElMessage.success(data.errMsg || i18n_t('reverseProxy.startSuccess'))
        } else {
          ElMessage.error(data.errMsg || i18n_t('reverseProxy.startFailed'))
        }

        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('reverseProxy.startFailed'))
      }
    }
    
    // 停止代理
    const stopProxy = async (item) => {
      try {
        const params = new URLSearchParams()
        params.append('name', item.name)
        params.append('flag', 'off')

        const response = await fetch(`${API_BASE_URL}/stop-reverse-proxy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('reverseProxy.stopFailed'))
        }

        const data = await response.json()
        if (data.errCode === 0) {
          ElMessage.success(data.errMsg || i18n_t('reverseProxy.stopSuccess'))
        } else {
          ElMessage.error(data.errMsg || i18n_t('reverseProxy.stopFailed'))
        }
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('reverseProxy.stopFailed'))
      }
    }
    
    // 删除代理
    const deleteProxy = (item) => {
      ElMessageBox.confirm(i18n_t('reverseProxy.deleteConfirm'), i18n_t('reverseProxy.confirm'), {
        confirmButtonText: i18n_t('reverseProxy.confirm'),
        cancelButtonText: i18n_t('reverseProxy.cancelText'),
        type: 'warning'
      }).then(async () => {
        try {
          const params = new URLSearchParams()
          params.append('name', item.name)

          const response = await fetch(`${API_BASE_URL}/delete-reverse-proxy`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            credentials: 'include',
            body: params
          })

          if (!response.ok) {
            throw new Error(i18n_t('reverseProxy.deleteFailed'))
          }

          const data = await response.json()
          if (data.errCode === 0) {
            ElMessage.success(data.errMsg || i18n_t('reverseProxy.deleteSuccess'))
          } else {
            ElMessage.error(data.errMsg || i18n_t('reverseProxy.deleteFailed'))
          }
          fetchData()
        } catch (error) {
          ElMessage.error(error.message || i18n_t('reverseProxy.deleteFailed'))
        }
      }).catch(() => {})
    }
    
    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }
    
    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success(i18n_t('reverseProxy.autoRefreshEnabled'))
      } else {
        stopLogAutoRefresh()
        ElMessage.info(i18n_t('reverseProxy.autoRefreshDisabled'))
      }
    }
    
    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }
    
    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }
    
    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }

        const params = new URLSearchParams({
          module: 'reverseproxy',
          page: logCurrentPage.value,
          size: logPageSize.value
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('reverseProxy.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('reverseProxy.pleaseLoginFirst'))
          router.push('/user-login?callback=reverse-proxy')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('reverseProxy.fetchLogsFailed'))
        }

        logsData.value = data.data || []
        logTotal.value = data.count || 0

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error(i18n_t('reverseProxy.fetchLogsError'), error)
        if (showLoading) {
          ElMessage.error(`${i18n_t('reverseProxy.fetchLogsFailed')}: ${error.message || i18n_t('reverseProxy.unknownError')}`)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }
    
    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }
    
    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(i18n_t('reverseProxy.confirmClearLogs'), i18n_t('reverseProxy.confirm'), {
          confirmButtonText: i18n_t('reverseProxy.confirm'),
          cancelButtonText: i18n_t('reverseProxy.cancelText'),
          type: 'warning'
        })

        logClearing.value = true

        const params = new URLSearchParams({
          module: 'reverseproxy'
        })

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('reverseProxy.networkError'))

        const data = await response.json()
        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('reverseProxy.pleaseLoginFirst'))
          router.push('/user-login?callback=reverse-proxy')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('reverseProxy.clearLogsFailed'))
        }

        ElMessage.success(i18n_t('reverseProxy.logsCleared'))
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error(i18n_t('reverseProxy.clearLogsFailed'), error)
          ElMessage.error(`${i18n_t('reverseProxy.clearLogsFailed')}: ${error.message || i18n_t('reverseProxy.unknownError')}`)
        }
      } finally {
        logClearing.value = false
      }
    }
    
    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true

        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'reverseproxy',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('reverseProxy.networkError'))

        const data = await response.json()
        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('reverseProxy.pleaseLoginFirst'))
          router.push('/user-login?callback=reverse-proxy')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('reverseProxy.fetchLogsFailed'))
        }

        const allLogs = data.data || []

        if (allLogs.length === 0) {
          ElMessage.warning(i18n_t('reverseProxy.noLogsToDownload'))
          return
        }

        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })

        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `reverse_proxy_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        ElMessage.success(i18n_t('reverseProxy.downloadSuccess'))
      } catch (error) {
        console.error(i18n_t('reverseProxy.downloadFailed'), error)
        ElMessage.error(`${i18n_t('reverseProxy.downloadFailed')}: ${error.message || i18n_t('reverseProxy.unknownError')}`)
      } finally {
        logDownloading.value = false
      }
    }
    
    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }
    
    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }
    
    // 刷新数据
    const refreshData = () => {
      fetchData()
    }
    
    // 停止自动刷新
    const stopAutoRefresh = () => {
      if (intervalId.value) {
        clearInterval(intervalId.value)
        intervalId.value = null
      }
      isEditingForm.value = true
    }
    
    // 开始自动刷新
    const startAutoRefresh = () => {
      isEditingForm.value = false
      if (!intervalId.value && !isEditingForm.value) {
        intervalId.value = setInterval(() => {
          fetchData()
        }, 5000)
      }
    }
    
    // 获取数据
    const fetchData = async () => {
      try {
        isLoading.value = true
        const response = await fetch(`${API_BASE_URL}/reverse-proxy`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(i18n_t('reverseProxy.fetchDataFailed'))
        }

        const result = await response.json()

        if (result.errCode === 1000) {
          ElMessage.error(i18n_t('reverseProxy.pleaseLoginFirst'))
          router.push('/user-login?callback=reverse-proxy')
          return
        }

        if (result.errCode != 0) {
          ElMessage.error(result.errMsg)
          return
        }

        proxyList.value = result.data?.reverseProxyPort || []
        isLogin.value = result.isLogin || false
        loginUsername.value = result.loginUsername || ''
        isAdmin.value = result.isAdmin || false

      } catch (error) {
        console.error(i18n_t('reverseProxy.fetchDataFailed'), error)
        ElMessage.error(error.message || i18n_t('reverseProxy.fetchDataFailed'))
        proxyList.value = []
      } finally {
        isLoading.value = false
      }
    }
    
    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }
    
    // 初始化数据
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchData()
      startAutoRefresh()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }
      stopLogAutoRefresh()
    })
    
    return {
      isMobile,
      proxyList,
      isLoading,
      dialogVisible,
      isEditing,
      proxyForm,
      proxyFormRef,
      formRules,
      isLogin,
      loginUsername,
      isAdmin,
      dialogTitle,
      showAddDialog,
      showEditDialog,
      handleCloseDialog,
      submitProxyForm,
      startProxy,
      stopProxy,
      deleteProxy,
      refreshData,
      getStatusClass,
      stopAutoRefresh,
      startAutoRefresh,
      // 日志相关
      logDialogVisible,
      showLogDialog,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped lang="scss">
.reverse-proxy-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  .card-header {
    padding: 20px;
    border-bottom: 1px solid #ebeef5;
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 8px;
    }
    
    .card-subtitle {
      font-size: 14px;
      color: #606266;
      margin: 0;
      
      .text-danger {
        color: #f56c6c;
        font-weight: 500;
      }
    }
  }
  
  .card-body {
    padding: 20px;
    
    .description-text {
      font-size: 14px;
      color: #606266;
      margin: 0;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      
      .card-title {
        font-size: 16px;
        margin-bottom: 4px;
      }
      
      .card-subtitle {
        font-size: 12px;
      }
    }
    
    .card-body {
      padding: 12px;
      
      .description-text {
        font-size: 12px;
      }
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
      
      .card-title {
        font-size: 15px;
      }
    }
    
    .card-body {
      padding: 8px;
    }
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.btn-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
  
  @media (max-width: 480px) {
    .el-button--small {
      padding: 6px 8px;
      font-size: 12px;
    }
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.proxy-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
  
  .stopped-row {
    opacity: 0.7;
    background-color: #fafafa;
    
    &:hover td {
      background-color: #f5f5f5;
    }
  }
}

.status-cell {
  max-width: 300px;

  .status-content {
    white-space: normal;
    word-break: break-word;
    
    &.running {
      color: #67c23a;
    }
    
    &.stopped {
      color: #909399;
    }
    
    &.failed {
      color: #f56c6c;
      font-weight: 500;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
}

.action-cell {
  white-space: nowrap;
  
  .action-buttons {
    display: flex;
    gap: 4px;
    flex-wrap: nowrap;
    
    .el-button {
      flex-shrink: 0;
    }
    
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      justify-content: flex-end;
    }
    
    .el-button--small {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .action-buttons {
      gap: 2px;
      
      .el-button--small {
        padding: 4px 6px;
        font-size: 11px;
      }
    }
  }
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
    animation: rotating 2s linear infinite;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式处理 */
@media (max-width: 768px) {
  .proxy-table {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
        font-size: 13px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &.action-cell {
        padding: 10px 12px;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
        
        .action-buttons {
          flex: 1;
          justify-content: flex-end;
        }
      }
    }
  }
  
  .proxy-dialog {
    :deep(.el-dialog) {
      margin: 8px !important;
    }
    
    :deep(.el-dialog__body) {
      padding: 16px;
    }
    
    :deep(.el-dialog__header) {
      padding: 16px;
    }
    
    :deep(.el-dialog__footer) {
      padding: 16px;
    }
  }
}

@media (max-width: 480px) {
  .proxy-table {
    td {
      padding: 8px 10px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
      
      &.action-cell {
        .action-buttons {
          .el-button--small {
            margin-bottom: 0;
          }
        }
      }
    }
  }
  
  .status-content {
    font-size: 12px;
  }
  
  .btn-group {
    .el-button--small {
      padding: 4px 6px;
      font-size: 11px;
    }
  }
}

/* Element Plus 对话框移动端适配 */
:deep(.el-dialog) {
  @media (max-width: 768px) {
    width: 95% !important;
    margin-top: 5vh !important;
  }
  
  @media (max-width: 480px) {
    width: 100% !important;
    max-height: 100%;
    border-radius: 0;
    
    .el-dialog__header {
      padding: 12px 16px;
    }
    
    .el-dialog__body {
      padding: 16px;
      overflow-y: auto;
      max-height: calc(100vh - 120px);
    }
    
    .el-dialog__footer {
      padding: 12px 16px;
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>